/**
 * Google Analytics 4 Event Tracking Utility
 * Tracks custom events throughout the AI Opportunity Audit
 * Includes enhanced e-commerce measurement
 * Includes Meta (Facebook) Pixel tracking
 */

// Helper function to send GA4 events
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
    console.log('GA Event:', eventName, eventParams);
  }
};

// Helper function for Meta Pixel
const fbq = (...args) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq(...args);
    console.log('FB Pixel:', ...args);
  }
};

// E-commerce Events
export const trackBeginCheckout = (company, industry, employeeCount, promoCode = null, price = 9.00) => {
  // GA4 e-commerce
  trackEvent('begin_checkout', {
    currency: 'USD',
    value: price,
    items: [{
      item_id: 'ai-opportunity-audit',
      item_name: 'AI Opportunity Audit™ - Digital Edition',
      item_category: 'Audit',
      price: price,
      quantity: 1,
      coupon: promoCode || undefined
    }],
    company,
    industry,
    employee_count: employeeCount,
    promo_code: promoCode || undefined
  });

  // Meta Pixel
  fbq('track', 'InitiateCheckout', {
    value: price,
    currency: 'USD',
    content_name: 'AI Opportunity Audit™',
    content_category: 'Audit'
  });
};

export const trackAddPaymentInfo = (paymentType = 'card', price = 9.00, promoCode = null) => {
  // GA4
  trackEvent('add_payment_info', {
    currency: 'USD',
    value: price,
    payment_type: paymentType,
    items: [{
      item_id: 'ai-opportunity-audit',
      item_name: 'AI Opportunity Audit™ - Digital Edition',
      price: price,
      quantity: 1,
      coupon: promoCode || undefined
    }],
    promo_code: promoCode || undefined
  });

  // Meta Pixel
  fbq('track', 'AddPaymentInfo');
};

export const trackPurchase = (transactionId, company, industry, employeeCount, role, price = 9.00, promoCode = null) => {
  // GA4 e-commerce
  trackEvent('purchase', {
    transaction_id: transactionId,
    value: price,
    tax: 0,
    shipping: 0,
    currency: 'USD',
    items: [{
      item_id: 'ai-opportunity-audit',
      item_name: 'AI Opportunity Audit™ - Digital Edition',
      item_category: 'Audit',
      item_category2: industry,
      price: price,
      quantity: 1,
      coupon: promoCode || undefined
    }],
    // Custom dimensions
    company,
    industry,
    employee_count: employeeCount,
    user_role: role,
    promo_code: promoCode || undefined
  });

  // Meta Pixel
  fbq('track', 'Purchase', {
    value: price,
    currency: 'USD',
    content_name: 'AI Opportunity Audit™',
    content_type: 'product',
    content_ids: ['ai-opportunity-audit']
  });
};

// Checkout Events (legacy - keeping for backwards compatibility)
export const trackCheckoutStarted = (testMode = false, promoCode = null) => {
  trackEvent('checkout_started', {
    test_mode: testMode,
    promo_code: promoCode || undefined
  });
};

export const trackCheckoutCompleted = (company, industry, employeeCount, paymentAmount) => {
  // This is now handled by trackPurchase but keeping for compatibility
  trackEvent('checkout_completed', {
    value: paymentAmount / 100,
    currency: 'USD',
    company,
    industry,
    employee_count: employeeCount
  });
};

// Audit Progress Events
export const trackAuditStarted = () => {
  trackEvent('audit_started', {
    timestamp: new Date().toISOString()
  });

  // Meta Pixel - ViewContent
  fbq('track', 'ViewContent', {
    value: 9.00,
    currency: 'USD',
    content_name: 'AI Opportunity Audit™',
    content_category: 'Audit'
  });
};

export const trackSectionCompleted = (sectionName, score) => {
  trackEvent('section_completed', {
    section: sectionName,
    score: score,
    max_score: 25
  });
};

export const trackROIItemAdded = (category, annual) => {
  trackEvent('roi_item_added', {
    category,
    annual_value: annual
  });
};

export const trackMatrixRated = (opportunityName, impact, difficulty) => {
  trackEvent('opportunity_rated', {
    opportunity: opportunityName,
    impact,
    difficulty
  });
};

// Save & Resume Events
export const trackSaveProgress = (email) => {
  trackEvent('save_progress', {
    email_provided: !!email
  });
};

export const trackResumeAudit = (token) => {
  trackEvent('resume_audit', {
    has_token: !!token
  });
};

// Report Events
export const trackReportGenerated = (auditId, overallScore, totalROI) => {
  trackEvent('report_generated', {
    audit_id: auditId,
    overall_score: overallScore,
    total_roi: totalROI
  });
};

export const trackPDFDownloaded = (auditId) => {
  trackEvent('pdf_downloaded', {
    audit_id: auditId
  });
};

// CTA Events
export const trackCTAClicked = (ctaName, destination) => {
  trackEvent('cta_clicked', {
    cta_name: ctaName,
    destination_url: destination
  });
};

export const trackScheduleCallClicked = () => {
  trackEvent('schedule_call_clicked', {
    cta_source: 'report_page'
  });
};

export const trackBookRoadmapClicked = () => {
  trackEvent('book_roadmap_clicked', {
    cta_source: 'report_page'
  });
};

// Navigation Events
export const trackPageNavigation = (fromPage, toPage) => {
  trackEvent('page_navigation', {
    from_page: fromPage,
    to_page: toPage
  });
};

// Error Events
export const trackError = (errorType, errorMessage) => {
  trackEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage
  });
};
